<template>
  <Badge v-if="isEmpty" variant="neutral" class="px-1">-</Badge>
  <span
    v-else
    class="relative min-w-0"
    :class="pretty ? 'group w-full basis-full' : isStructured ? 'group max-w-full' : 'max-w-full'"
  >
    <Badge
      :variant="variant"
      class="min-w-0 whitespace-pre-wrap break-words px-1 text-left"
      :class="
        pretty
          ? '!w-full !max-w-full cursor-pointer'
          : isStructured
            ? '!w-auto !max-w-full cursor-pointer'
            : '!w-auto !max-w-full'
      "
      v-on="isStructured ? { click: togglePretty } : {}"
    >
      {{ displayText }}
    </Badge>
    <span
      v-if="isStructured"
      class="pointer-events-none absolute right-1.5 top-1.5 z-10 rounded bg-white/80 px-1 font-sans text-[10px] font-medium leading-none text-obsidian-600 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
    >
      {{ pretty ? 'raw' : 'format' }}
    </span>
  </span>
</template>

<script setup lang="ts">
import Badge from '@/components/Badge.vue';
import { computed, ref } from 'vue';

const props = defineProps<{
  value: unknown;
  variant: 'problem' | 'success';
}>();

const pretty = ref(false);

const isEmpty = computed(
  () => props.value === null || props.value === undefined || props.value === '',
);

const structured = computed(() => {
  if (typeof props.value === 'object' && props.value !== null) {
    return props.value;
  }
  if (typeof props.value === 'string') {
    const trimmed = props.value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return JSON.parse(trimmed) as unknown;
      } catch {
        return null;
      }
    }
  }
  return null;
});

const isStructured = computed(() => structured.value !== null);

const displayText = computed(() => {
  if (structured.value !== null) {
    const json = JSON.stringify(structured.value, null, pretty.value ? 2 : undefined);
    // Soft wraps at JSON punctuation so tokens stay intact (no break-all).
    return pretty.value ? json : json.replace(/([,:])/g, '$1\u200b');
  }
  if (typeof props.value === 'string') {
    return props.value;
  }
  return String(props.value);
});

function togglePretty(): void {
  pretty.value = !pretty.value;
}
</script>
